import { useContext, useEffect, useState } from "react";

import { 
  editVesselRule,
  createVesselRule,
  deleteVesselRule,
  deleteVesselConnection,
  editVesselProperties,
  createVesselProperties,
  updateVesselProperties
} from "../../services/Vessel";
import AuthContext from "../../context/AuthContext";
import SnackbarContext from "../../context/SnackbarContext";
import IntegrationHelp from "../IntegrationHelp";

import * as React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from "@mui/styles";
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import DeleteIcon from '@mui/icons-material/Delete';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

import PostAddIcon from '@mui/icons-material/PostAdd';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';

const createModalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 280,
  backgroundColor: "background.paper",
  borderRadius: "6px",
  boxShadow: 24,
  p: 4,
};

const useStyles = makeStyles({
  headerBox: {
    color: "#95a2b4",
    fontSize: "13px",
    fontWeight: "bold",
  },
  sectionHeader: {
    "&.MuiTypography-root": {
      color: "#3d4849",
      fontSize: "16px",
      fontWeight: 700,
      padding: "20px",
    },
  },
  section: {
    fontSize: "14px",
    margin: "20px 40px 40px 40px",
  },
  saveButton: {
    width: 160,
    "&.MuiButton-root": {
      fontWeight: "bold",
      background: "#07A89E",
      marginLeft: "12px",
      "&:hover": {
        background: "#0A8F87",
      },
      "&:disabled": {
        background: "#cdeeec"
      }
    },
  }
});

const avatarStyle = {
  backgroundColor: "#009cf8",
};

const modelSpecificsToolTip = "For each Revenue Targeting Model (with a rule associated with it) there must be fields in the downstream platform to put the Model Name and the RevScore. This must be present on both companies and contacts."
const fieldLabelsToolTip = "Map our data points to your preferred field in the downstream platform."

export default function IntegrationDetails({revenueTargetingModels, connection, getConnection}) {
  const auth = useContext(AuthContext);
	const snackbar = useContext(SnackbarContext);

  const classes = useStyles();

  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [refreshValue, setRefreshValue] = useState(0);

  const [createRuleOpen, setCreateRuleOpen] = useState(false);
  const [deleteRuleOpen, setDeleteRuleOpen] = useState(false);
  const [deleteConnectionOpen, setDeleteConnectionOpen] = useState(false);
  const [deleteRule, setDeleteRule] = useState({});
  const [createModelSelected, setCreateModelSelected] = useState(false);
  const [createModel, setCreateModel] = useState({});
  const [createBtnLoading, setCreateBtnLoading] = useState(false);

  const [refreshButtonLoading, setRefreshButtonLoading] = useState(false);
  const [saveBtnLoading, setSaveBtnLoading] = useState(false);
  const [modelSpecifics, setModelSpecifics] = useState([]);
  const [generalProperties, setGeneralProperties] = useState([]);

  const handleOpen = () => setCreateRuleOpen(true);
  const handleClose = () => setCreateRuleOpen(false);

  const handleDeleteConnection = () => {
    deleteVesselConnection(
      auth.token, auth.userAccountID, connection.connection_id
    ).then(response => {
      if (response.ok) {
        snackbar.createSuccess('Delete Successful');
      }
    }).finally(()=>{getConnection();})
  };

  useEffect(() => {
    getConnection();
  }, [refreshValue]);

  useEffect(() => {
    setModelSpecifics(connection?.properties?.model_specifics);
    setGeneralProperties(connection?.properties?.general);
  }, [connection, refreshValue]);

  const createBtnDisabled = () => {
    return !createModelSelected;
  };

  const loadingBtnDisabled = () => {
    let null_specifics = modelSpecifics?.filter(prop => prop.key === null);
    let null_generals = generalProperties?.filter(prop => prop.key === null);

    if (null_generals?.length + null_specifics?.length === 0) {
      return false
    } else {
      return true
    }
  };

  const handleCreateRule = () => {
    setCreateBtnLoading(true);
    let rule_body = {
      connection_id: connection.connection_id,
      rtm_id: createModel.id
    }
    createVesselRule(
      auth.token, auth.userAccountID, rule_body
    ).then((response) => {
      if (response.ok) {
        handleClose();
        snackbar.createSuccess('Rule successfully created.');
        setRefreshValue(refreshValue + 1);
      } else {
        snackbar.createError('Rule could not be created.');
      }
    }).finally(setCreateBtnLoading(false));
  };

  const handleDeleteRule = () => {
    let rule_body = {
      rule_id: deleteRule.id
    }
    deleteVesselRule(
      auth.token, auth.userAccountID, rule_body
    ).then((response) => {
      if (response.ok) {
        setDeleteRuleOpen(false);
        snackbar.createSuccess('Rule successfully deleted.');
        setRefreshValue(refreshValue + 1);
      } else {
        snackbar.createError('Rule could not be deleted.');
      }
    }).finally(setCreateBtnLoading(false));
  };

  const handleUpdateProperties = () => {
    setRefreshButtonLoading(true);
    updateVesselProperties(auth.token, auth.userAccountID, connection.connection_id
      ).then((response) => {
        if (response.ok) {
          snackbar.createSuccess('Properties options will be updated shortly. (a few seconds)');
        } else {
          snackbar.createError('Properties options could not be refreshed.');
        }
      }).finally(() => {
        setRefreshButtonLoading(false);
        setTimeout(()=>{setRefreshValue(r => r + 1);}, 1000);
      })
  };

  const handleSaveProperties = () => {
    setSaveBtnLoading(true);
    let num_requests = connection.rules.length;

    try {
      if (!connection.properties.model_specifics[0].key) {
        connection.rules.forEach(rule => {
          let specific_props = modelSpecifics.filter(prop => prop.revenue_targeting_model === rule.model_id);
          let propertyBody = {
            connection_id: connection.connection_id,
            rtm_id: rule.model_id,
            model: specific_props.find(prop => prop.name.startsWith("Model Name")).key,
            rev_score: specific_props.find(prop => prop.name.startsWith("RevScore")).key,
            revenue_band: generalProperties.find(prop => prop.name === "Revenue Band").key,
            employee_band: generalProperties.find(prop => prop.name === "Employee Band").key,
            funding_raised: generalProperties.find(prop => prop.name === "Funding Raised").key,
            linkedin_url_company: generalProperties.find(prop => prop.name === "Company LinkedIn URL").key,
            contact_model: specific_props.find(prop => prop.name.startsWith("Contacts Model Name")).key,
            contact_rev_score: specific_props.find(prop => prop.name.startsWith("Contacts RevScore")).key,
            functions: generalProperties.find(prop => prop.name === "Functions").key,
            seniorities: generalProperties.find(prop => prop.name === "Seniorities").key,
            linkedin_url_contact: generalProperties.find(prop => prop.name === "Contact LinkedIn URL").key
          }
          if (connection.platform_type === 'crm') {
            propertyBody.city = generalProperties.find(prop => prop.name === "City").key
            propertyBody.state = generalProperties.find(prop => prop.name === "State").key
            propertyBody.country = generalProperties.find(prop => prop.name === "Country").key
          }
          if (connection.integration_id === 'pipedrive') {
            propertyBody.industry = generalProperties.find(prop => prop.name === "Industry").key
            propertyBody.domain = generalProperties.find(prop => prop.name === "Domain").key
            propertyBody.job_title = generalProperties.find(prop => prop.name === "Job Title").key

          }
          createVesselProperties(auth.token, auth.userAccountID, propertyBody
          ).then((response) => {
            if (response.ok) {
              num_requests --
            } else {
              snackbar.createError('Properties could not be created.');
            }
            if (num_requests === 0) {snackbar.createSuccess('Properties updated successfully')}
          }).finally(() => {
            setSaveBtnLoading(false);
          });
        });
      } else {
        connection.rules.forEach(rule => {
          let specific_props = modelSpecifics.filter(prop => prop.revenue_targeting_model === rule.model_id);
          let propertyBody = {
            connection_id: connection.connection_id,
            rtm_id: rule.model_id,
            model: specific_props.find(prop => prop.name.startsWith("Model Name")).key,
            rev_score: specific_props.find(prop => prop.name.startsWith("RevScore")).key,
            revenue_band: generalProperties.find(prop => prop.name === "Revenue Band").key,
            employee_band: generalProperties.find(prop => prop.name === "Employee Band").key,
            funding_raised: generalProperties.find(prop => prop.name === "Funding Raised").key,
            linkedin_url_company: generalProperties.find(prop => prop.name === "Company LinkedIn URL").key,
            contact_model: specific_props.find(prop => prop.name.startsWith("Contacts Model Name")).key,
            contact_rev_score: specific_props.find(prop => prop.name.startsWith("Contacts RevScore")).key,
            functions: generalProperties.find(prop => prop.name === "Functions").key,
            seniorities: generalProperties.find(prop => prop.name === "Seniorities").key,
            linkedin_url_contact: generalProperties.find(prop => prop.name === "Contact LinkedIn URL").key
          }
          if (connection.platform_type === 'crm') {
            propertyBody.city = generalProperties.find(prop => prop.name === "City").key
            propertyBody.state = generalProperties.find(prop => prop.name === "State").key
            propertyBody.country = generalProperties.find(prop => prop.name === "Country").key
          }
          if (connection.integration_id === 'pipedrive') {
            propertyBody.industry = generalProperties.find(prop => prop.name === "Industry").key
            propertyBody.domain = generalProperties.find(prop => prop.name === "Domain").key
            propertyBody.job_title = generalProperties.find(prop => prop.name === "Job Title").key

          }
          editVesselProperties(auth.token, auth.userAccountID, propertyBody
          ).then((response) => {
              if (response.ok) {
                num_requests --
              } else {
                snackbar.createError('Properties could not be updated.');
              }
              if (num_requests === 0) {snackbar.createSuccess('Properties updated successfully')}
            }).finally(() => {
              setSaveBtnLoading(false);
            });
        });
      }
    }
    catch (error) {
      snackbar.createError('Properties could not be updated.');
      setSaveBtnLoading(false);
    }
    
  };

  function Row(props) {
    const { row } = props;
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(false);
  
    return (
      <React.Fragment>
        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          {edit ? (
            <>
              <TableCell component="th" scope="row">
                {row.name}
              </TableCell>
              <TableCell align="right">{row.id}</TableCell>
              <TableCell align="right">{row.integration_id.charAt(0).toUpperCase() + row.integration_id.slice(1)}</TableCell>
              <TableCell align="right">
                <Autocomplete
                    ChipProps={{
                      color: "primary",
                    }}
                    loading={connectionsLoading}
                    options={['random']}
                    getOptionLabel={(rule_type) => rule_type.charAt(0).toUpperCase() + rule_type.slice(1)}
                    value={row.rule_type || null}
                    onChange={(event, newValue) => {
                      if (newValue) {row.rule_type = newValue;}
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label="Rule Type"
                      />
                    )}
                  />
                </TableCell>
              <TableCell>
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => {
                    let ruleBody = {
                      id: row.id,
                      rtm_id: row.modelId,
                      rule_type: row.rule_type,
                      crm_users : row.users
                    }
                    editVesselRule(
                      auth.token, auth.userAccountID, ruleBody
                    ).then((response) => {
                      if (response.ok) {
                        snackbar.createSuccess("Success saving rule.");
                      } else {
                        snackbar.createError("Error saving rule.");
                      }
                    }).finally(() => {
                      setRefreshValue(refreshValue + 1);
                      setEdit(false);
                      setOpen(false);
                    });
                  }}
                >
                  <DoneIcon />
                </IconButton>
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={() => {
                    setEdit(false);
                    setOpen(false);
                    setRefreshValue(refreshValue + 1);
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </TableCell>
            </>
          ) : (
            <>
              <TableCell component="th" scope="row">
                {row.name}
              </TableCell>
              <TableCell align="right">{row.id}</TableCell>
              <TableCell align="right">{row.integration_id.charAt(0).toUpperCase() + row.integration_id.slice(1)}</TableCell>
              <TableCell align="right">{row.rule_type.charAt(0).toUpperCase() + row.rule_type.slice(1)}</TableCell>
              <TableCell>
                {open ? (
                  <>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => setEdit(true)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => {setDeleteRuleOpen(true);setDeleteRule(row);}}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                  
                ) : (
                  <>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => {setOpen(true);setEdit(true);}}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => {setDeleteRuleOpen(true);setDeleteRule(row);}}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </TableCell>
            </>
          )}
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Users
                </Typography>
                {edit ? (
                  <Autocomplete
                    ChipProps={{
                      color: "primary",
                    }}
                    multiple
                    options={row.userOptions ?? []}
                    defaultValue={row.users ?? []}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.vessel_id === value.vessel_id}
                    onChange={(event, newValue) => {
                      row.users = newValue;
                    }}
                    renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label=""
                    />
                  )}
                />
                ) : (
                  <Autocomplete
                    ChipProps={{
                      color: "primary",
                    }}
                    readOnly
                    multiple
                    options={row.userOptions ?? []}
                    defaultValue={row.users ?? []}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.vessel_id === value.vessel_id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label=""
                      />
                    )}
                  />
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };
  
  Row.propTypes = {
    row: PropTypes.shape({
      id: PropTypes.number.isRequired,
      modelId: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      integration_id: PropTypes.string.isRequired,
      rule_type: PropTypes.string.isRequired,
      users: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          vessel_id: PropTypes.string.isRequired
        }),
      ).isRequired,
      userOptions: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          vessel_id: PropTypes.string.isRequired
        }),
      ).isRequired,
    }).isRequired,
  };

  return (
    <Box>
      {/* Status and buttons */}
      <Toolbar sx={{ padding: "0 !important" }}>
        <Box sx={{mr:2}}>
          <Typography variant="h5">{connection?.integration_id?.charAt(0).toUpperCase() + connection?.integration_id?.slice(1)}</Typography>
        </Box>
        <Box  flex={1}>
          <Chip
            label={connection.status?.replace('_', ' ')}
            color="primary"
            variant="outlined"
          />
        </Box>
        <IntegrationHelp />
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpen}
          sx={{ width: 160, fontWeight: "bold", margin: "2px 12px 0 0"}}
        >
          Create New Rule
        </Button>
        <Modal open={createRuleOpen} onClose={handleClose}>
          <Box sx={createModalStyles}>
            <Grid align="center">
              <Avatar style={avatarStyle}>
                <PostAddIcon />
              </Avatar>
            </Grid>
            <Stack spacing={2}>
              <TextField
                variant="standard"
                label="Integration ID"
                value={connection?.integration_id?.charAt(0).toUpperCase() + connection?.integration_id?.slice(1)}
                InputProps={{
                  readOnly: true,
                }}
              />
              <Autocomplete
                ChipProps={{
                  color: "primary",
                }}
                defaultValue={null}
                options={revenueTargetingModels}
                getOptionLabel={(rtm) => rtm.name}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setCreateModelSelected(true);
                    setCreateModel(newValue);
                  } else {
                    setCreateModelSelected(false);
                    setCreateModel({});
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label="Model"
                  />
                )}
              />
              <Button
                disabled={createBtnDisabled()}
                onClick={handleCreateRule}
                type="submit"
                color="primary"
                variant="contained"
                fullWidth
              >
                Create Rule
              </Button>
            </Stack>
          </Box>
        </Modal>
        <Modal open={deleteRuleOpen} onClose={()=>setDeleteRuleOpen(false)}>
          <Box sx={createModalStyles}>
            <Grid align="center">
              <Avatar style={avatarStyle}>
                <DeleteIcon />
              </Avatar>
            </Grid>
            <Stack spacing={2} alignItems="center">
              <Typography align="center" sx={{width:8/10}}>Are you sure you want to delete this rule?</Typography>
              <Button
                onClick={() => setDeleteRuleOpen(false)}
                type="submit"
                color="primary"
                variant="contained"
                fullWidth
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteRule}
                type="submit"
                color="error"
                variant="contained"
                fullWidth
              >
                Delete Rule
              </Button>
            </Stack>
          </Box>
        </Modal>
        <Button
          variant="contained"
          color="error"
          startIcon={<PowerSettingsNewIcon />}
          onClick={() => {setDeleteConnectionOpen(true)}}
          sx={{ width: 160, fontWeight: "bold"}}
        >
          Turn Off
        </Button>
        <Modal open={deleteConnectionOpen} onClose={()=>setDeleteConnectionOpen(false)}>
          <Box sx={createModalStyles}>
            <Grid align="center">
              <Avatar style={avatarStyle}>
                <DeleteIcon />
              </Avatar>
            </Grid>
            <Stack spacing={2} alignItems="center">
              <Typography align="center" sx={{width:8/10}}>Are you sure you want to delete this connection?</Typography>
              <Button
                onClick={() => setDeleteConnectionOpen(false)}
                type="submit"
                color="primary"
                variant="contained"
                fullWidth
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConnection}
                type="submit"
                color="error"
                variant="contained"
                fullWidth
              >
                Delete Connection
              </Button>
            </Stack>
          </Box>
        </Modal>
      </Toolbar>
      {/* Rule count */}
      <Toolbar sx={{ padding: "0 !important" }}>
        <Box className={classes.headerBox} flex={1}>
          Rules:
          <br />
          <span style={{ color: "#009cf8", fontSize: "18px" }}>
            {(connection.rules?.length) || 0}
          </span>
        </Box>
      </Toolbar>
      {/* Rule table */}
      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Model Name</TableCell>
              <TableCell align="right">Rule ID</TableCell>
              <TableCell align="right">Integration ID</TableCell>
              <TableCell align="right" sx={{width: 200}}>Rule Type</TableCell>
              <TableCell sx={{width:80}}/>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(connection).length > 0 && (connection.rules.map(item  => (
              <Row 
                key={item.rule_id}
                row={{
                  id: item.rule_id,
                  modelId: item.model_id,
                  name: item.model_name,
                  integration_id: connection.integration_id,
                  rule_type: item.rule_type,
                  users: item.crm_user_ids,
                  userOptions: connection.user_options
                }} />
            )))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Properties toolbar */}
      <Toolbar sx={{ mt:1, padding: "0 !important" }}>
        <Box flex={1}>
          <Typography variant="h5">Properties</Typography>
        </Box>
        <LoadingButton
          onClick={handleUpdateProperties}
          loading={refreshButtonLoading}
          color="primary"
          variant="contained"
        >
          Refresh Properties
        </LoadingButton>
        <LoadingButton
          disabled={loadingBtnDisabled()}
          onClick={handleSaveProperties}
          loading={saveBtnLoading}
          className={classes.saveButton}
          variant="contained"
        >
          Save Progress
        </LoadingButton>
      </Toolbar>
      {/* Properties stack */}
      <Stack spacing={3}>
        <Card>
          <Typography className={classes.sectionHeader}>
            Model Specifics
            <Tooltip
              title={modelSpecificsToolTip}
              arrow
              placement="bottom-start"
            >
              <InfoIcon color="primary" sx={{fontSize: "20px",position: "absolute",padding: "3px 0 0 3px"}}/>
            </Tooltip>
          </Typography>
          <Divider />
          <CardContent>
            <Box className={classes.section}>
              <Grid 
                container 
                spacing={4}
                direction="row"
                alignItems="center"
              >
                {Object.keys(connection).length > 0 && (modelSpecifics?.map(item  => (
                  <Grid item xs={6}>
                    <Autocomplete
                      ChipProps={{
                        color: "primary",
                      }}
                      loading={connectionsLoading}
                      options={
                        item.object === 'company'
                          ? (connection?.account_property_options ? [...connection?.account_property_options?.filter(prop => item?.types?.indexOf(prop.type) > -1), {key:null, name:''}] : [{key:null, name:''}])
                          : (connection?.contact_property_options ? [...connection?.contact_property_options?.filter(prop => item?.types?.indexOf(prop.type) > -1), {key:null, name:''}] : [{key:null, name:''}])
                      }
                      isOptionEqualToValue={(option, value) => option.name === value.name}
                      getOptionLabel={(property) => property.name}
                      value={
                        item.object === 'company'
                         ? connection?.account_property_options?.find((prop) => prop.key === item.key) || {key:null, name:''}
                         : connection?.contact_property_options?.find((prop) => prop.key === item.key) || {key:null, name:''}
                      }
                      onChange={(event, newValue) => {
                        console.log(modelSpecifics)
                        console.log(newValue)
                        setModelSpecifics(modelSpecifics.map(prop => {
                          if (prop.name === item.name) {
                            if (newValue === null) {newValue={key:null}}
                            return {key: newValue.key, name: item.name, object: item.object, revenue_targeting_model:item.revenue_targeting_model, types:item.types};
                          } else {
                            return prop;
                          }
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          label={item.name}
                        />
                      )}
                    />
                  </Grid>
                )))}
              </Grid>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <Typography className={classes.sectionHeader}>
            Field Labels
            <Tooltip
              title={fieldLabelsToolTip}
              arrow
              placement="bottom-start"
            >
              <InfoIcon color="primary" sx={{fontSize: "20px",position: "absolute",padding: "3px 0 0 3px"}}/>
            </Tooltip>
          </Typography>
          <Divider />
          <CardContent>
            <Box className={classes.section}>
              <Grid 
                container
                spacing={4}
                direction="row"
                alignItems="center"
              >
                {Object.keys(connection).length > 0 && (generalProperties?.map(item  => (
                  <Grid item xs={6}>
                    <Autocomplete
                      ChipProps={{
                        color: "primary",
                      }}
                      loading={connectionsLoading}
                      options={
                        item.object === 'company'
                         ? (connection?.account_property_options ? [...connection?.account_property_options?.filter(prop => item?.types?.indexOf(prop.type) > -1), {key:null, name:''}] : [{key:null, name:''}])
                         : (connection?.contact_property_options ? [...connection?.contact_property_options?.filter(prop => item?.types?.indexOf(prop.type) > -1), {key:null, name:''}] : [{key:null, name:''}])
                      }
                      isOptionEqualToValue={(option, value) => option.name === value.name}
                      getOptionLabel={(property) => property.name}
                      value={
                        item.object === 'company'
                          ? connection.account_property_options?.find((prop) => prop.key === item.key) || {key:null, name:''}
                          : connection.contact_property_options?.find((prop) => prop.key === item.key) || {key:null, name:''}
                      }
                      onChange={(event, newValue) => {
                        setGeneralProperties(generalProperties.map(prop => {
                          if (prop.name === item.name) {
                            if (newValue === null) {newValue={key:null}}
                            return {key: newValue.key, name: item.name, object: item.object, revenue_targeting_model:item.revenue_targeting_model, types:item.types};
                          } else {
                            return prop;
                          }
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          label={item.name + ' (' + item.object + ')'}
                        />
                      )}
                    />
                  </Grid>
                )))}
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  )
}